import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Star } from 'lucide-react';
import { apiBaseUrl } from '../config/runtime';

export interface Review {
  id: number;
  trade_id: number;
  reviewer_id: number;
  reviewee_id: number;
  rating: number;
  comment: string;
  role: string;
  reviewer_nickname: string;
  reviewer_name: string;
  reviewer_image?: string;
  trade_title?: string;
  created_at: string;
}

interface ReviewListProps {
  userId?: number;
  tradeId?: number;
  compact?: boolean;
}

const StarRating: React.FC<{ rating: number; size?: number }> = ({ rating, size = 14 }) => (
  <div style={{ display: 'flex', gap: 2 }}>
    {[1, 2, 3, 4, 5].map(n => (
      <Star
        key={n}
        size={size}
        fill={n <= rating ? '#F59E0B' : 'none'}
        stroke={n <= rating ? '#F59E0B' : '#DDD'}
        strokeWidth={1.5}
      />
    ))}
  </div>
);

const ReviewList: React.FC<ReviewListProps> = ({ userId, tradeId, compact = false }) => {
  const { t, i18n } = useTranslation();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [meta, setMeta] = useState<{ total: number; avgRating: number; page: number; totalPages: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const loadReviews = useCallback(async () => {
    setLoading(true);
    try {
      let url = '';
      if (tradeId) url = apiBaseUrl(`/reviews/trade/${tradeId}`);
      else if (userId) url = apiBaseUrl(`/reviews/user/${userId}?page=${page}`);
      else return;
      const res = await axios.get(url);
      if (res.data.success) {
        setReviews(res.data.data);
        if (res.data.meta) setMeta(res.data.meta);
      }
    } catch (err) {
      console.error('[ReviewList] 리뷰 로드 실패:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, tradeId, page]);

  useEffect(() => {
    if (!userId && !tradeId) return;
    loadReviews();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadReviews]);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now.getTime() - d.getTime()) / 1000);
    if (diff < 60) return t('notifications.justNow');
    if (diff < 3600) return t('notifications.minutesAgo', { n: Math.floor(diff / 60) });
    if (diff < 86400) return t('notifications.hoursAgo', { n: Math.floor(diff / 3600) });
    if (diff < 2592000) return t('notifications.daysAgo', { n: Math.floor(diff / 86400) });
    return d.toLocaleDateString(i18n.language === 'ko' ? 'ko-KR' : 'en-US');
  };

  if (loading) return (
    <div style={{ padding: '24px', textAlign: 'center', color: '#BBB', fontSize: 14 }}>{t('reviewList.loading')}</div>
  );

  if (!reviews.length) return (
    <div style={{ padding: compact ? '16px 0' : '32px', textAlign: 'center', color: '#BBB', fontSize: 14 }}>
      {t('reviewList.empty')}
    </div>
  );

  return (
    <div>
      {/* 요약 (userId 기반일 때만) */}
      {meta && !compact && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 16,
          padding: '16px 20px', background: '#FAFAFA', borderRadius: 12,
          border: '1px solid #F0F0F0', marginBottom: 16,
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 36, fontWeight: 800, color: '#111', letterSpacing: '-1px' }}>
              {Number(meta.avgRating).toFixed(1)}
            </div>
            <StarRating rating={Math.round(meta.avgRating)} size={16} />
          </div>
          <div style={{ color: '#888', fontSize: 13 }}>
            <strong style={{ color: '#111' }}>{t('reviewList.totalN', { n: meta.total })}</strong>
          </div>
        </div>
      )}

      {/* 리뷰 목록 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {reviews.map(review => {
          const initial = (review.reviewer_nickname || review.reviewer_name || '?').charAt(0).toUpperCase();
          return (
            <div key={review.id} style={{
              padding: compact ? '12px 0' : '16px 20px',
              background: compact ? 'transparent' : '#fff',
              borderRadius: compact ? 0 : 12,
              border: compact ? 'none' : '1px solid #F0F0F0',
              borderBottom: compact ? '1px solid #F5F5F5' : undefined,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%',
                    background: '#4F46E5', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, fontWeight: 700, flexShrink: 0,
                  }}>
                    {review.reviewer_image
                      ? <img src={review.reviewer_image} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                      : initial}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#111' }}>
                      {review.reviewer_nickname || review.reviewer_name}
                      <span style={{ fontSize: 11, color: '#AAA', marginLeft: 6, fontWeight: 400 }}>
                        {review.role === 'client' ? t('nav.client') : t('nav.expert')}
                      </span>
                    </div>
                    <StarRating rating={review.rating} />
                  </div>
                </div>
                <span style={{ fontSize: 12, color: '#BBB', flexShrink: 0 }}>
                  {formatDate(review.created_at)}
                </span>
              </div>
              <p style={{ fontSize: 14, color: '#444', lineHeight: 1.65, margin: 0, paddingLeft: 40 }}>
                {review.comment}
              </p>
              {review.trade_title && !compact && (
                <div style={{ fontSize: 12, color: '#BBB', marginTop: 6, paddingLeft: 40 }}>
                  {t('reviewList.trade')}: {review.trade_title}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* 페이지네이션 */}
      {meta && meta.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{ padding: '7px 14px', border: '1.5px solid #E8E8E8', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}
          >{t('common.prev')}</button>
          <span style={{ padding: '7px 14px', fontSize: 13, color: '#666' }}>
            {page} / {meta.totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(meta.totalPages, p + 1))}
            disabled={page === meta.totalPages}
            style={{ padding: '7px 14px', border: '1.5px solid #E8E8E8', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}
          >{t('common.next')}</button>
        </div>
      )}
    </div>
  );
};

export default ReviewList;
